/**
 * React context plumbing for `AppData`, plus the `useNotificationToasts`
 * subscription hook.
 *
 * Split from `appdata.tsx` so non-React server-shaped helpers can import the
 * data class without pulling in React. `useNotificationToasts` resubscribes
 * whenever `AppData` itself changes (which is rare but happens in tests) and
 * tolerates a null context for components rendered outside the provider.
 */

import React, { useCallback, useEffect, useState } from 'react';

import type { AppData } from './appdata';
import type { NotificationToast } from './stdtypes';

export const AppDataContext = React.createContext<AppData | null>(null);

export function useAppData() {
    return React.useContext(AppDataContext) as AppData;
}

export function useNotificationToasts() {
    const [toasts, setToasts] = useState<NotificationToast[]>([]);
    const appData = useAppData();

    const updateToasts = useCallback(() => {
        setToasts(appData ? appData.getNotificationToasts() : []);
    }, [appData]);

    useEffect(() => {
        updateToasts();
        if (!appData) {
            return;
        }
        const unsubscribe = appData.subscribeToNotificationToasts(updateToasts);
        return unsubscribe;
    }, [appData, updateToasts]);

    return toasts;
}
